import {
  Divider,
  FormControl,
  FormHelperText,
  FormLabel,
  Modal,
  ModalClose,
  ModalDialog,
  Stack,
  styled,
  Typography,
} from '@healthinal/ui';
import { useQueryClient } from '@tanstack/react-query';
import { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { toast } from 'react-toastify';
import {
  getGetAddressContactsQueryOptions,
  getGetAddressesQueryOptions,
} from '../../../../../api/generated/address-controller/address-controller.ts';
import {
  AddressContactDto,
  AddressDto,
  AddressDtoAddressType,
  PatientAddressDtoReferenceType,
  TherapyDto,
} from '../../../../../api/generated/model';
import {
  getGetTherapyByPatientQueryKey,
  useUpdateTherapyByPatient,
} from '../../../../../api/generated/patient-controller/patient-controller.ts';
import { FormActionButtons } from '../../../../../components/form/FormActionButtons.tsx';
import { GermanDateInput } from '../../../../../components/form/GermanDateInput.tsx';
import { InstitutionContactFields } from '../../../../../components/form/InstitutionContactFields.tsx';
import { getCurrentDate, getDateAfterInputValue } from '../../../../../utils/date.ts';

interface FormValues {
  startPhase1: string;
  startPhase2: string;
  end: string;
  phase1DoctorAddress: AddressDto | null;
  phase1DoctorContact: AddressContactDto | null;
  phase2DoctorAddress: AddressDto | null;
  phase2DoctorContact: AddressContactDto | null;
}

export function AdjustTherapyDialog({
  open,
  onClose,
  currentTherapy,
}: {
  open: boolean;
  onClose: () => void;
  currentTherapy: TherapyDto;
}) {
  const queryClient = useQueryClient();
  const {
    handleSubmit,
    control,
    reset,
    setValue,
    watch,
    formState: { errors, isDirty: isFormDirty },
  } = useForm<FormValues>({
    defaultValues: {
      startPhase1: currentTherapy.startPhase1 ?? '',
      startPhase2: currentTherapy.startPhase2 ?? '',
      end: currentTherapy.end ?? '',
      phase1DoctorContact: null,
      phase1DoctorAddress: null,
      phase2DoctorAddress: null,
      phase2DoctorContact: null,
    },
    mode: 'onChange',
    reValidateMode: 'onChange',
  });

  const updateTherapyMutation = useUpdateTherapyByPatient({
    mutation: {
      onSuccess: () => {
        void queryClient.invalidateQueries({
          queryKey: getGetTherapyByPatientQueryKey(currentTherapy.patientId),
        });
        void queryClient.resetQueries(getGetAddressesQueryOptions());
        void queryClient.resetQueries(getGetAddressContactsQueryOptions(phase1Address?.id ?? ''));
        void queryClient.resetQueries(getGetAddressContactsQueryOptions(phase2Address?.id ?? ''));
        void queryClient.resetQueries({ queryKey: ['/api/addresses/contacts/list'] });
        onClose();
      },
      onError: () => {
        toast.error('Bei dem Speichern der Therapie ist ein Fehler aufgetreten');
      },
    },
  });

  const isPhase2Active = !!currentTherapy.startPhase2;
  const phase1Address = watch('phase1DoctorAddress');
  const phase2Address = watch('phase2DoctorAddress');
  const phase1Contact = watch('phase1DoctorContact');
  const phase2Contact = watch('phase2DoctorContact');

  const startPhase1Value = watch('startPhase1');
  const startPhase2Value = watch('startPhase2');

  const startPhase2EndMinDate = getDateAfterInputValue(startPhase2Value);
  const therapyEndMinDate = startPhase2EndMinDate ?? getDateAfterInputValue(startPhase1Value);

  useEffect(() => {
    if (open) {
      reset({
        startPhase1: currentTherapy.startPhase1 ?? '',
        startPhase2: currentTherapy.startPhase2 ?? '',
        end: currentTherapy.end ?? '',
        phase1DoctorAddress: currentTherapy.phase1Doctor?.address ?? null,
        phase1DoctorContact: currentTherapy.phase1Doctor?.contact ?? null,
        phase2DoctorAddress: currentTherapy.phase2Doctor?.address ?? null,
        phase2DoctorContact: currentTherapy.phase2Doctor?.contact ?? null,
      });
    }
  }, [open, currentTherapy]);

  const handleOnClose = () => {
    reset();
    onClose();
  };

  return (
    <Modal open={open} onClose={handleOnClose}>
      <ModalDialog sx={{ width: 800 }}>
        <ModalClose />
        <Typography level={'h4'}>Therapie anpassen</Typography>
        <Divider />
        <form
          onSubmit={handleSubmit((data) =>
            updateTherapyMutation.mutate({
              id: currentTherapy.patientId,
              data: {
                id: currentTherapy.id,
                patientId: currentTherapy.patientId,
                therapyMedication: currentTherapy.therapyMedication,
                finished: currentTherapy.finished,
                therapyResult: currentTherapy.therapyResult,
                resultNote: currentTherapy.resultNote,
                startPhase1: data.startPhase1,
                startPhase2: data.startPhase2,
                end: data.end,
                phase1Doctor: {
                  address: data.phase1DoctorAddress!,
                  contact: data.phase1DoctorContact ?? undefined,
                  from: getCurrentDate(),
                  patientId: currentTherapy.patientId,
                  referenceType: PatientAddressDtoReferenceType.THERAPY_PHASE_1,
                },
                phase2Doctor: data.phase2DoctorAddress
                  ? {
                      address: data.phase2DoctorAddress,
                      contact: data.phase2DoctorContact ?? undefined,
                      from: getCurrentDate(),
                      patientId: currentTherapy.patientId,
                      referenceType: PatientAddressDtoReferenceType.THERAPY_PHASE_2,
                    }
                  : undefined,
              },
            }),
          )}>
          <Stack gap={2}>
            <FormGrid>
              <FormControl error={!!errors.startPhase1}>
                <FormLabel>
                  <Typography>Therapie Start *</Typography>
                </FormLabel>
                <GermanDateInput
                  control={control}
                  name="startPhase1"
                  rules={{
                    required: true,
                  }}
                />
                {errors.startPhase1 && <FormHelperText>Therapie Start muss ausgefüllt werden</FormHelperText>}
              </FormControl>
              {isPhase2Active && (
                <FormControl error={!!errors.startPhase2}>
                  <FormLabel>
                    <Typography>Start Phase 2 *</Typography>
                  </FormLabel>
                  <GermanDateInput
                    control={control}
                    name="startPhase2"
                    rules={{
                      required: isPhase2Active,
                    }}
                  />
                  {errors.startPhase2 && <FormHelperText>Phase 2 wurde bereits gestartet</FormHelperText>}
                </FormControl>
              )}
              <FormControl error={!!errors.end}>
                <FormLabel>
                  <Typography>Therapie Ende</Typography>
                </FormLabel>
                <GermanDateInput
                  control={control}
                  name="end"
                  rules={{
                    required: false,
                    ...(therapyEndMinDate ? { min: { value: therapyEndMinDate, message: 'range' } } : {}),
                  }}
                  min={therapyEndMinDate}
                />
                {errors.end && <FormHelperText>Therapie Ende muss nach dem Start liegen</FormHelperText>}
              </FormControl>
              {isPhase2Active && <div />}
              <InstitutionContactFields
                formMethods={{ control, watch, setValue }}
                layout="fragment"
                addressField="phase1DoctorAddress"
                contactField="phase1DoctorContact"
                addressLabel="Institutionen Phase 1 *"
                contactLabel="Ansprechpersonen Phase 1"
                addressTypes={[AddressDtoAddressType.ACCOMMODATION_ASYLUM]}
                canChangeAddressType={false}
                contactId={phase1Contact?.id ?? undefined}
                addressRules={{ required: true }}
                addressError={!!errors.phase1DoctorAddress}
                addressHelperText={
                  errors.phase1DoctorAddress ? 'Institution für Phase 1 muss angegeben sein' : undefined
                }
                clearContactOnAddressChange="when-empty"
                useAllContactsList={true}
              />
              {isPhase2Active && (
                <>
                  <InstitutionContactFields
                    formMethods={{ control, watch, setValue }}
                    layout="fragment"
                    addressField="phase2DoctorAddress"
                    contactField="phase2DoctorContact"
                    addressLabel="Institutionen Phase 2 *"
                    contactLabel="Ansprechpersonen Phase 2"
                    addressTypes={[AddressDtoAddressType.DOCTOR]}
                    canChangeAddressType={false}
                    contactId={phase2Contact?.id ?? undefined}
                    addressRules={{ required: true }}
                    addressError={!!errors.phase2DoctorAddress}
                    addressHelperText={
                      errors.phase2DoctorAddress ? 'Institution für Phase 2 muss angegeben werden' : undefined
                    }
                    clearContactOnAddressChange="when-empty"
                    useAllContactsList={true}
                  />
                </>
              )}
            </FormGrid>
            <FormActionButtons
              onAbortClick={handleOnClose}
              disableSubmit={!isFormDirty}
              isLoading={updateTherapyMutation.isPending}
            />
          </Stack>
        </form>
      </ModalDialog>
    </Modal>
  );
}

const FormGrid = styled('div')`
  display: grid;
  gap: 0.75rem 1rem;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  align-items: start;
`;
